import React, { useState } from 'react';
import { Mail, Phone, BookOpen, Calendar, School, ChevronDown, ChevronUp, Award, Hash } from 'lucide-react';
import TestResultsChart from './TestResultsChart';
import type { AcademicRecord } from '@conceptwaves/shared-components';

interface StudentCardProps {
  record: AcademicRecord;
  hideContactInfo?: boolean;
}

const StudentCard: React.FC<StudentCardProps> = ({ record, hideContactInfo = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { StudentInfo, TestInfo } = record;

  // Calculate overall performance
  const calculateOverallScore = () => {
    if (!TestInfo || TestInfo.length === 0) return null;

    const totalScored = TestInfo.reduce((sum, test) => sum + test.totalScored, 0);
    const totalMax = TestInfo.reduce((sum, test) => sum + test.totalMaxMarks, 0);

    return totalMax > 0 ? ((totalScored / totalMax) * 100).toFixed(1) : 0;
  };

  const overallScore = calculateOverallScore();
  const hasTests = TestInfo && TestInfo.length > 0;

  return (
    <div className="student-card">
      {/* Header */}
      <div className="student-card-header">
        <div className="student-header-content">
          <div>
            <h3 className="student-name">{StudentInfo.FullName}</h3>
            <p className="student-course">{StudentInfo.CourseName}</p>
          </div>
          {overallScore !== null && (
            <div className="overall-score-badge">
              <div className="score-value">{overallScore}%</div>
              <div className="score-label">Overall</div>
            </div>
          )}
        </div>
      </div>

      {/* Student Info */}
      <div className="student-card-body">
        <div className="student-info-grid">
          {StudentInfo.StudentCode && (
            <div className="info-item">
              <Hash size={18} className="info-icon icon-purple" />
              <div>
                <div className="info-label">Student Code</div>
                <div className="info-value">{StudentInfo.StudentCode}</div>
              </div>
            </div>
          )}

          {!hideContactInfo && (
            <div className="info-item">
              <Mail size={18} className="info-icon icon-blue" />
              <div>
                <div className="info-label">Email</div>
                <div className="info-value">{StudentInfo.Email}</div>
              </div>
            </div>
          )}

          {!hideContactInfo && (
            <div className="info-item">
              <Phone size={18} className="info-icon icon-green" />
              <div>
                <div className="info-label">Mobile</div>
                <div className="info-value">{StudentInfo.Mobile}</div>
              </div>
            </div>
          )}

          <div className="info-item">
            <School size={18} className="info-icon icon-purple" />
            <div>
              <div className="info-label">School</div>
              <div className="info-value">{StudentInfo.SchoolName}</div>
            </div>
          </div>

          <div className="info-item">
            <Calendar size={18} className="info-icon icon-orange" />
            <div>
              <div className="info-label">Year & Batch</div>
              <div className="info-value">
                {StudentInfo.YearName} • {StudentInfo.BatchName}
              </div>
            </div>
          </div>
        </div>

        {/* Test Summary */}
        {hasTests && (
          <div className="test-summary">
            <div className="test-header">
              <div className="test-title">
                <Award size={18} className="icon-yellow" />
                <span>Test Results ({TestInfo.length})</span>
              </div>
              <button
                onClick={() => setExpanded(!expanded)}
                className="expand-button"
              >
                {expanded ? (
                  <>
                    <span>Hide Details</span>
                    <ChevronUp size={18} />
                  </>
                ) : (
                  <>
                    <span>Show Details</span>
                    <ChevronDown size={18} />
                  </>
                )}
              </button>
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              {TestInfo.slice(0, 3).map((test, idx) => {
                const percentage = ((test.totalScored / test.totalMaxMarks) * 100).toFixed(0);
                return (
                  <div key={idx} className="stat-box">
                    <div className="stat-value">{percentage}%</div>
                    <div className="stat-label">{test.testName}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!hasTests && (
          <div className="no-tests">
            No test results available
          </div>
        )}
      </div>

      {/* Expanded Test Details */}
      {expanded && hasTests && (
        <div className="test-details">
          <TestResultsChart tests={TestInfo} studentName={StudentInfo.FullName} />
        </div>
      )}
    </div>
  );
};

export default StudentCard;
